import userStore from '@/store/UserStore'
import appStore from '@/store/AppStore'
import projectStore from '@/store/ProjectStore'
import { CharacteristicType, ProductType } from '@/helper/Types'
import activityTrackingService from '@/services/ActivityTrackingService'
import ProjectBaseData from '@/services/models/ProjectBaseData'
import { ProjectResponse } from '@/services/models/ProjectResponse'
import BrandMapping from '@/helper/BrandMapping'
import { HPCharacteristic } from '@/services/models/HPCharacteristic.ts'

window.dataLayer = []

export function tagStepView() {
  const url = '/' + appStore.getStepModelByHpStep(appStore.currentStep).tag

  // Google Analytics
  window.dataLayer.push({
    event: 'pageView',
    pageURL: url,
    contactId: userStore.contactId
  })

  tagPageView(url)
}

export function tagPageView(url: string) {
  if (window.hj) {
    window.hj('stateChange', url)
  }

  if (window.ineum) {
    window.ineum('page', url)
    window.ineum('meta', 'hostName', window.location?.hostname)
  }
}

export function tagProjectStore(project: ProjectBaseData) {
  if (!window.ineum) {
    return
  }

  window.ineum('meta', 'postalCode', project.projectAddress?.postalCode)
  window.ineum('meta', 'city', project.projectAddress?.city)

  const projectResponse = project as ProjectResponse
  if (projectResponse.id) {
    window.ineum('meta', 'projectId', projectResponse.id)
  }

  if (project.projectAddress?.altitude) {
    window.ineum('meta', 'altitude', project.projectAddress?.altitude)
  }
}

export function tagUserStore() {
  if (!window.ineum) {
    return
  }

  if (userStore.contactId) {
    window.ineum('user', userStore.contactId)
    window.ineum('meta', 'companyId', userStore.companyId)
    window.ineum('meta', 'privileged', userStore.isPrivileged)
    window.ineum('meta', 'brand', userStore.brand)
    window.ineum('meta', 'country', userStore.country)
  }

  if (userStore.isoLocale) {
    window.ineum('meta', 'language', userStore.isoLocale.substring(0, 2))
  }

  if (userStore.realm) {
    window.ineum('meta', 'realm', userStore.realm)
  }
}

export function tagDownload() {
  const heatPump = projectStore.getSelectedProduct(ProductType.HEATPUMP)
  const heatPumpChr = projectStore.getProductCharacteristic(heatPump, CharacteristicType.HEAT_PUMP) as HPCharacteristic
  const indoorUnit = projectStore.getSelectedProduct(ProductType.ADDITIONAL_PRODUCT)?.name
    || projectStore.getArticleByCharacteristic(CharacteristicType.APPLIANCE_INTERFACE)?.details?.name
    || 'none'
  window.dataLayer.push({
    event: 'genericEvent',
    eventCategory: 'System Configurator',
    eventAction: 'Download configuration',
    eventLabel: [
      userStore.isoLocale.toUpperCase().substring(0, 2),
      projectStore.projectProperties.projectPostalCode.val as string,
      projectStore.getSelectedProductName(ProductType.HEATPUMP),
      projectStore.getSelectedProductName(ProductType.BACKUPHEATER),
      projectStore.getSelectedProductName(ProductType.DHWTANK),
      projectStore.getSelectedProductName(ProductType.BUFFERTANK),
      userStore.brand,
      projectStore.projectProperties.systemSchemeDocumentNumber.val || 'none',
      projectStore.totalPrice,
      indoorUnit,
      `heating ${projectStore.isHeating}`,
      `cooling ${projectStore.isCooling}`,
      heatPumpChr.cascadeSize
    ].join('_')
  })

  window.dataLayer.push({
    event: 'ssd_button',
    buttonType: 'download_pdf',
    schemeNumber: `${projectStore.getSelectedProductName(ProductType.HEATPUMP)} | ${
      projectStore.projectProperties.systemSchemeId.val
    }`
  })
}

export function tagActivity() {
  window.dataLayer.push({
    event: 'activityEvent',
    eventValue: activityTrackingService.interval,
    eventCategory: 'System Configurator',
    step: appStore.getStepModelByHpStep(appStore.currentStep).tag,
    projectId: projectStore.projectId,
    realm: userStore.realm,
    companyId: userStore.companyId,
    privileged: userStore.isPrivileged,
    brand: userStore.brand,
    country: userStore.country
  })
}

export function activateConsentManager() {

  if (userStore.companyId && userStore.companyId.startsWith('e2e-test-')) {
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.classList.add('cmplazyload')

  if (BrandMapping.getBrandShortHand(userStore.brand) === 'sd') {
    script.setAttribute('src', '/static/consent-manager/sdgb.js')
  } else {
    script.setAttribute('src', '/static/consent-manager/vaillant.js')
  }

  script.setAttribute('data-cmp-host', 'd.delivery.consentmanager.net')
  script.setAttribute('data-cmp-cdn', 'cdn.consentmanager.net')
  script.setAttribute('data-cmp-codesrc', '1')
  document.head.appendChild(script)

  window.cmp_setlang = userStore.country === 'XX' ? 'en-GB' : userStore.currentIsoLocale
}

export function activateTagManager() {
  activityTrackingService.initialize()

  window.dataLayer = [
    { countryCode: userStore.country, brand: userStore.brand },
    { 'gtm.start': new Date().getTime(), event: 'gtm.js' }
  ]

  const script = document.createElement('script')
  script.async = true
  script.classList.add('cmplazyload')
  script.setAttribute('data-cmp-src', 'https://www.googletagmanager.com/gtm.js?id=GTM-KP42X7')
  script.setAttribute('data-cmp-vendor', 's905')
  document.head.appendChild(script)
}

export function isProduction() {
  return location.hostname.toLowerCase() === 'systemconfigurator.vaillant-group.com'
}

export function activateHotjar() {
  const url = new URL(window.location.href)

  if (!isProduction() || url.searchParams.has('token')) {
    return
  }

  initHotjar(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')
}

export function hj(event: string) {
  if (window.hj) {
    window.hj('event', event)
  }
}

function initHotjar(h: Window, o: Document, t: string, j: string) {
  h.hj = h.hj
    || function () {
      h.hj.q = []
      // eslint-disable-next-line prefer-rest-params
      h.hj.q.push(arguments)
    }

  h._hjSettings = {
    hjid: 2336476,
    hjsv: 6
  }

  const script = document.createElement('script')
  script.async = true
  script.classList.add('cmplazyload')
  script.setAttribute('data-cmp-src', t + h._hjSettings.hjid + j + h._hjSettings.hjsv)
  script.setAttribute('data-cmp-vendor', 's46')
  document.head.appendChild(script)
}

export function activateInstana() {
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return
  }

  window['InstanaEumObject'] = 'ineum'
  window.ineum = (a: string, b: any, c?: any) => console.trace('ineum', a, b, c)
  window.ineum.q = [
    ['reportingUrl', 'https://eum-blue-saas.instana.io'],
    ['key', 'u1syZ36wQeqDITF4BR6Ltg']
  ]
  window.ineum.v = 2
  window.ineum.l = new Date().getTime()

  const script = document.createElement('script')
  script.defer = true
  script.crossOrigin = 'anonymous'
  script.classList.add('cmplazyload')
  script.setAttribute('data-cmp-src', 'https://eum.instana.io/eum.min.js')
  script.setAttribute('data-cmp-vendor', 'c32761')
  document.head.appendChild(script)

  window.ineum('secrets', [/token/i, /password/i, /firstName/i, /lastName/i, /street/i, /houseNumber/i])
}
